import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Link
} from '@mui/material';
import {
  Person,
  Phone,
  Visibility,
  VisibilityOff,
  VpnKey
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { authAPI } from '../../utils/api';
import { appConfig } from '../../config/app';

const Authentication = () => {
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [step, setStep] = useState(1); // 1: form, 2: otp
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  
  const { actions } = useApp();
  const navigate = useNavigate();

  const handleModeChange = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setStep(1);
    setFormData({ name: '', mobile: '', otp: '' });
    setError('');
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        if (!formData.name.trim()) {
          setError('Name is required');
          setLoading(false);
          return;
        }
        if (!formData.mobile.trim()) {
          setError('Mobile number is required');
          setLoading(false);
          return;
        }
        
        const response = await authAPI.register({
          name: formData.name.trim(),
          mobile: formData.mobile.trim()
        });
        
        if (response.data.success) {
          setStep(2);
          actions.showSnackbar('OTP sent successfully', 'success');
        }
      } else {
        if (!formData.mobile.trim()) {
          setError('Mobile number is required');
          setLoading(false);
          return;
        }
        
        const response = await authAPI.login({
          mobile: formData.mobile.trim()
        });
        
        if (response.data.success) {
          setStep(2);
          actions.showSnackbar('OTP sent successfully', 'success');
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.otp.trim()) {
        setError('OTP is required');
        setLoading(false);
        return;
      }

      const response = await authAPI.validateOtp({
        mobile: formData.mobile.trim(),
        otp: formData.otp.trim()
      });

      if (response.data.success) {
        // Store token and user data
        localStorage.setItem(appConfig.storage.token, response.data.token);
        actions.setUser(response.data.user);
        
        actions.showSnackbar(
          mode === 'signup' ? 'Account created successfully!' : 'Logged in successfully!',
          'success'
        );
        
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <Box component="form" onSubmit={handleSubmitForm} sx={{ mt: 2 }}>
      {mode === 'signup' && (
        <TextField
          fullWidth
          label="Full Name"
          variant="outlined"
          margin="normal"
          value={formData.name}
          onChange={handleInputChange('name')}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            )
          }}
        />
      )}
      
      <TextField
        fullWidth
        label="Mobile Number"
        variant="outlined"
        margin="normal"
        value={formData.mobile}
        onChange={handleInputChange('mobile')}
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone />
            </InputAdornment>
          )
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={loading}
        size="large"
      >
        {loading ? 'Please wait...' : `Send OTP`}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleModeChange}
            sx={{ 
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {mode === 'login' ? 'Sign up' : 'Login'}
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  const renderOtpForm = () => (
    <Box component="form" onSubmit={handleVerifyOtp} sx={{ mt: 2 }}>
      <Typography variant="body1" sx={{ mb: 2 }} color="text.secondary">
        Enter the 4-digit OTP sent to {formData.mobile}
      </Typography>
      
      <TextField
        fullWidth
        label="Enter OTP"
        variant="outlined"
        margin="normal"
        value={formData.otp}
        onChange={handleInputChange('otp')}
        required
        inputProps={{ maxLength: 4 }}
        type={showOtp ? 'text' : 'password'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKey />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowOtp(!showOtp)}
                edge="end"
              >
                {showOtp ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={loading}
        size="large"
      >
        {loading ? 'Verifying...' : 'Verify OTP'}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep(1)}
        sx={{ mb: 1 }}
      >
        Back to {mode === 'signup' ? 'Sign Up' : 'Login'}
      </Button>
    </Box>
  );

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 4
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <img 
            src={require('../../assets/logo.png')} 
            alt="Logo" 
            style={{
              height: '60px',
              width: 'auto',
              maxWidth: '100%'
            }}
            onError={(e) => {
              // Fallback to pilot.png if logo.png doesn't exist
              e.target.src = require('../../assets/pilot.png');
            }}
          />
        </Box>

        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              component="h1"
              variant="h4"
              align="center"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              {step === 1 ? (mode === 'signup' ? 'Sign Up' : 'Login') : 'Verify OTP'}
            </Typography>

            {step === 1 && (
              <Typography variant="body1" align="center" color="text.secondary" paragraph>
                {mode === 'signup' 
                  ? 'Create your account to get started' 
                  : 'Sign in to your account'
                }
              </Typography>
            )}

            {step === 1 ? renderForm() : renderOtpForm()}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Authentication; 